import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import DateAvailabilityModal from '../Dashboard/DateAvailabilityModal';
import { getDisplayStatus } from '../utils/date-utils';
import { EventHoverCardPortal } from './EventHoverCard';
import EventClickPopup from './EventClickPopup';
import { useThemeStore } from '../stores/useThemeStore';
import DayModeToggle from '../components/DayModeToggle';
import FlameIcon from '../components/FlameIcon';
import { accentForEvent } from './event-accent';
import { drivesOut } from '../components/TransportChips';
import dayjs from 'dayjs';
import PageLayout from '../components/ui/PageLayout';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { IconChevronLeft, IconChevronRight } from '../components/ui/icons';

const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAYS_FULL  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_LANES  = 20; // effectively unlimited — row height is flex-distributed

/* ------------------------------------------------------------------ */
/*  Status palette — yellow=pending, green=confirmed, red=cancelled    */
/* ------------------------------------------------------------------ */
const STATUS = {
  pending:   { bar: 'bg-amber-400/20 day:bg-amber-100',     text: 'text-amber-300 day:text-amber-700',   dot: 'bg-amber-400 day:bg-amber-500',   ring: 'ring-amber-400/25 day:ring-amber-300',   label: 'Pending' },
  confirmed: { bar: 'bg-emerald-400/20 day:bg-emerald-100', text: 'text-emerald-300 day:text-emerald-700', dot: 'bg-emerald-400 day:bg-emerald-500', ring: 'ring-emerald-400/25 day:ring-emerald-300', label: 'Confirmed' },
  packing:   { bar: 'bg-purple-400/20 day:bg-purple-100',   text: 'text-purple-300 day:text-purple-700',  dot: 'bg-purple-400 day:bg-purple-500',  ring: 'ring-purple-400/25 day:ring-purple-300',  label: 'Packing' },
  active:    { bar: 'bg-sky-400/20 day:bg-sky-100',         text: 'text-sky-300 day:text-sky-700',         dot: 'bg-sky-400 day:bg-sky-500',         ring: 'ring-sky-400/25 day:ring-sky-300',         label: 'Active' },
  completed: { bar: 'bg-[rgb(95_95_95/0.15)] day:bg-gray-200/60',    text: 'text-on-surface-variant day:text-gray-600',       dot: 'bg-outline day:bg-gray-400',       ring: 'ring-[rgb(95_95_95/0.25)] day:ring-gray-300',       label: 'Completed' },
  cancelled: { bar: 'bg-red-500/15 day:bg-red-50',          text: 'text-red-400/60 day:text-red-700',      dot: 'bg-red-500 day:bg-red-600',         ring: 'ring-red-500/25 day:ring-red-300',         label: 'Cancelled' },
};

const RENTAL_STATUS = {
  bar: 'bg-orange-400/20 day:bg-orange-100', text: 'text-orange-300 day:text-orange-700', dot: 'bg-orange-400 day:bg-orange-500', ring: 'ring-orange-400/25 day:ring-orange-300', label: 'Rental',
};

const LEASE_STATUS = {
  bar: 'bg-blue-400/20 day:bg-blue-100', text: 'text-blue-300 day:text-blue-700', dot: 'bg-blue-400 day:bg-blue-500', ring: 'ring-blue-400/25 day:ring-blue-300', label: 'Lease',
};

const ALL_STATUSES = Object.keys(STATUS);
const DEFAULT_ACTIVE = new Set(['pending', 'confirmed', 'packing', 'active']);
const CALENDAR_PREFS_KEY = 'eventCalendarPrefs';

const loadCalendarPrefs = () => {
  try {
    const raw = localStorage.getItem(CALENDAR_PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/*  Heat-map glow                                                      */
/* ------------------------------------------------------------------ */
const getHeat = (drones) => {
  if (drones >= 500) return { bg: 'bg-red-500/[0.06] day:bg-red-100/60',     glow: 'shadow-[inset_0_0_24px_rgba(239,68,68,0.07)]',  ring: 'ring-1 ring-inset ring-red-500/15 day:ring-red-300/60' };
  if (drones >= 200) return { bg: 'bg-amber-500/[0.04] day:bg-amber-100/70', glow: 'shadow-[inset_0_0_18px_rgba(245,158,11,0.05)]', ring: 'ring-1 ring-inset ring-amber-500/10 day:ring-amber-300/60' };
  if (drones > 0)    return { bg: 'bg-sky-500/[0.03] day:bg-sky-100/60',     glow: 'shadow-[inset_0_0_14px_rgba(14,165,233,0.04)]',  ring: 'ring-1 ring-inset ring-sky-500/8 day:ring-sky-300/50' };
  return { bg: '', glow: '', ring: '' };
};

/* ------------------------------------------------------------------ */
/*  Per-event / per-tour accent color                                  */
/*  Status (confirmed / pending / packing / active / completed /        */
/*  cancelled) drives the bar background. This accent tints the text,  */
/*  ring, dashed lines, and chain icons so events are distinguishable  */
/*  at a glance — tour_id supersedes event id so tour members share    */
/*  one color and visually clump together across week rows.            */
/*                                                                     */
/*  The palette moved to ./event-accent.ts so this page and the Tour   */
/*  Timeline cannot drift apart on what color a tour is. That match   */
/*  used to be two copies of one hash function, which is to say it     */
/*  was not enforced at all.                                          */
/* ------------------------------------------------------------------ */

// Max gap (days) between an event and its ship_out_date / ship_back_date that the calendar
// will trust as real data. Anything beyond this is treated as a typo (e.g. year mistyped as
// '0026' instead of '2026') and rendered without the wing. Also used to pad the calendar
// fetch window so events whose ship window leaks into adjacent months still load.
const SHIP_DATE_MAX_DAYS = 60;

/* ------------------------------------------------------------------ */
/*  Bar affordances: show time, airspace, travel notes                 */
/*  All three were already in the /events/calendar payload (it selects  */
/*  events.*) and simply never rendered. The bar is 22px tall with 10px */
/*  text, so each one has to earn its width.                           */
/* ------------------------------------------------------------------ */

/** "21:30" → "9:30p", "09:00" → "9a". The bar cannot afford "9:00 PM". */
const compactTime = (t) => {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
  if (!m) return String(t);
  let h = Number(m[1]);
  const min = m[2];
  const suffix = h >= 12 ? 'p' : 'a';
  h = h % 12 || 12;
  return min === '00' ? `${h}${suffix}` : `${h}:${min}${suffix}`;
};

/**
 * Airspace marks. Three glyphs:
 *
 *   !  act on this   (needs_action, needs_authorization)
 *   ?  not reviewed  (needs_review)
 *   ·  waiting       (pending — submitted, the FAA has it)
 *
 * `?` covers `needs_review`, which is the DEFAULT every event is created with and
 * 80% of non-cancelled events in prod (86 of 108) — so it lands on roughly four
 * bars in five. That was raised with Mannie explicitly, with the number, and he
 * kept it: an untriaged show IS worth seeing, and the density is the honest
 * picture of the backlog rather than a rendering problem. Do not "clean it up"
 * without asking again.
 *
 * `complete` is the one state with no mark — nothing to do, nothing to say.
 *
 * `!` is shared by the two act-now states rather than splitting them across two
 * shapes, because urgency is carried by color there and one fewer glyph to learn
 * is worth more than the distinction. Text glyphs, not color alone, so the signal
 * survives a printed or screenshotted production meeting.
 */
const AIRSPACE_MARKS = {
  needs_action: { glyph: '!', label: 'Airspace needs action', cls: 'text-red-300 day:text-red-700' },
  needs_authorization: { glyph: '!', label: 'Airspace authorization needed', cls: 'text-amber-300 day:text-amber-700' },
  needs_review: { glyph: '?', label: 'Airspace not reviewed yet', cls: 'text-amber-300/70 day:text-amber-600' },
  pending: { glyph: '·', label: 'Airspace submitted, waiting on the FAA', cls: 'text-sky-300 day:text-sky-700' },
};
const airspaceMark = (status) => AIRSPACE_MARKS[status] || null;

/**
 * Every travel note about this show, from BOTH places a producer can type one.
 *
 * Two families, deliberately kept distinct and labeled by origin rather than
 * merged: `events.transport_*_note` is typed on the Event page, while
 * `event_vehicle.drive_*_note` / `note` / `origin_label` is typed on the tour
 * timeline against a specific truck. Same subject, different table, different
 * author. The second family used to render nowhere but the grid that wrote it.
 */
const travelNoteText = (event) => {
  const parts = [
    event.transport_out_note && `Out: ${event.transport_out_note}`,
    event.transport_back_note && `Back: ${event.transport_back_note}`,
  ];
  for (const v of event.vehicles || []) {
    const who = v.name || v.display_code || 'Vehicle';
    if (v.origin_label) parts.push(`${who} from ${v.origin_label}`);
    if (v.drive_out_note) parts.push(`${who} drive out: ${v.drive_out_note}`);
    if (v.drive_back_note) parts.push(`${who} drive back: ${v.drive_back_note}`);
    if (v.note) parts.push(`${who}: ${v.note}`);
  }
  const kept = parts.filter(Boolean);
  return kept.length ? kept.join('\n') : null;
};

/** Compact truck label for a bar. Human name over display code, because the fleet
 *  calls them "Van 2" and "Pacifica"; a meet-up truck becomes a "+1". */
const vehicleLabel = (event) => {
  const list = event.vehicles || [];
  if (!list.length) return null;
  const first = list[0].name || list[0].display_code;
  return list.length > 1 ? `${first} +${list.length - 1}` : first;
};

// Chain-link icon — shared between tour event bars, the day hover popover, and the
// Empty Tour Weeks toggle. Color inherits from currentColor; size/spacing via className.
const ChainIcon = ({ className = 'w-3 h-3' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
  </svg>
);

/* buffered drone count for one event */
const buffered = (e) => {
  const ls = e.ls_drone_count || 0, py = e.pyro_drone_count || 0, b = e.buffer_percentage || 0;
  return ls + Math.ceil(ls * b / 100) + py + Math.ceil(py * b / 100);
};

/* ------------------------------------------------------------------ */
/*  Tour spans across the visible event set                            */
/*  Lets computeWeekLayout know which tours continue past each row's   */
/*  Sun/Sat edge so the lane bg + gap connector can extend to the      */
/*  row boundary instead of stopping at the in-week tour members.      */
/* ------------------------------------------------------------------ */
function buildTourSpans(allEvents, activeStatuses) {
  const spans = {};
  // Pass 1: establish first/last event of each tour from ALL members (regardless of
  // status filter). Otherwise a middle event becomes "first visible" when earlier
  // members are hidden as completed/cancelled, and wrongly inherits the ship-out wing.
  allEvents.forEach(event => {
    if (!event.tour_id) return;
    if (event._isLease || event._isRental) return;
    const evS = dayjs(event.event_date);
    const evE = event.end_date ? dayjs(event.end_date) : evS;
    const tid = event.tour_id;
    if (!spans[tid]) {
      spans[tid] = {
        firstEventId: event.id,
        lastEventId:  event.id,
        _firstDate: evS,
        _lastDate:  evE,
        minDate: null,  // filled in pass 2 — visible bounds only
        maxDate: null,
      };
    } else {
      // Strict comparisons + lexicographic event.id tiebreaker so the choice of
      // first/last is deterministic and meaningful even when two members share a date.
      const firstIsBefore = evS.isBefore(spans[tid]._firstDate);
      const firstIsTied   = evS.isSame(spans[tid]._firstDate, 'day');
      if (firstIsBefore || (firstIsTied && event.id < spans[tid].firstEventId)) {
        spans[tid]._firstDate = evS;
        spans[tid].firstEventId = event.id;
      }
      const lastIsAfter = evE.isAfter(spans[tid]._lastDate);
      const lastIsTied  = evE.isSame(spans[tid]._lastDate, 'day');
      if (lastIsAfter || (lastIsTied && event.id > spans[tid].lastEventId)) {
        spans[tid]._lastDate = evE;
        spans[tid].lastEventId = event.id;
      }
    }
  });
  // Pass 2: visible bounds drive cross-week lane extension + empty-tour-week phantoms.
  // A completed tour event in an off-screen row shouldn't make the lane bg sprawl.
  allEvents.forEach(event => {
    if (!event.tour_id) return;
    if (event._isLease || event._isRental) return;
    if (!activeStatuses.has(getDisplayStatus(event))) return;
    const span = spans[event.tour_id];
    if (!span) return;
    const evS = dayjs(event.event_date);
    const evE = event.end_date ? dayjs(event.end_date) : evS;
    if (!span.minDate || evS.isBefore(span.minDate)) span.minDate = evS;
    if (!span.maxDate || evE.isAfter(span.maxDate))  span.maxDate = evE;
  });
  return spans;
}

/* ------------------------------------------------------------------ */
/*  Compute lane assignments for a week of events                      */
/*                                                                     */
/*  Returns { placements, overflowByCol }                              */
/*   placements[]: { event, startCol, endCol, isStart, isEnd, lane }   */
/*   overflowByCol: { col: count }                                     */
/* ------------------------------------------------------------------ */
function computeWeekLayout(weekStartDate, allEvents, activeStatuses, tourSpans = {}, opts = {}) {
  const { showEmptyTourWeeks = false, showShipDates = false, showTourLanes = true } = opts;
  const weekEnd = weekStartDate.add(6, 'day');
  const candidates = [];

  allEvents.forEach(event => {
    if (!event._isLease && !event._isRental && !activeStatuses.has(getDisplayStatus(event))) return;
    const evS = dayjs(event.event_date);
    const evE = event.end_date ? dayjs(event.end_date) : evS;

    // Effective bar range — when showShipDates is on, expand to include ship_out/ship_back.
    // For tour events, only the FIRST event in the tour gets ship-out and only the LAST gets
    // ship-back — gear travels with the tour bus between events, so middle stops don't ship.
    // Non-tour events get both as normal.
    const tourSpan = event.tour_id ? tourSpans[event.tour_id] : null;
    const isFirstTourEvent = tourSpan ? tourSpan.firstEventId === event.id : false;
    const isLastTourEvent  = tourSpan ? tourSpan.lastEventId  === event.id : false;
    const useShipOut  = !tourSpan || isFirstTourEvent;
    const useShipBack = !tourSpan || isLastTourEvent;
    // Directional ship-date clamp: ship_out must be on/before the event start, ship_back
    // on/after the event end, both within SHIP_DATE_MAX_DAYS. Rejects year-typos (e.g.
    // '0026-05-12') AND reversed-direction data that would otherwise render a phantom wing.
    const shipOutRaw  = (showShipDates && useShipOut  && event.ship_out_date)  ? dayjs(event.ship_out_date)  : null;
    const shipBackRaw = (showShipDates && useShipBack && event.ship_back_date) ? dayjs(event.ship_back_date) : null;
    const shipOut  = (shipOutRaw  && !shipOutRaw.isAfter(evS, 'day')   && evS.diff(shipOutRaw, 'day')   <= SHIP_DATE_MAX_DAYS) ? shipOutRaw  : null;
    const shipBack = (shipBackRaw && !shipBackRaw.isBefore(evE, 'day') && shipBackRaw.diff(evE, 'day') <= SHIP_DATE_MAX_DAYS) ? shipBackRaw : null;
    const effEvS = (shipOut  && shipOut.isBefore(evS, 'day'))   ? shipOut  : evS;
    const effEvE = (shipBack && shipBack.isAfter(evE, 'day'))   ? shipBack : evE;

    if (effEvS.isAfter(weekEnd, 'day') || effEvE.isBefore(weekStartDate, 'day')) return;

    const startCol = effEvS.isBefore(weekStartDate, 'day') ? 0 : effEvS.diff(weekStartDate, 'day');
    const endCol   = effEvE.isAfter(weekEnd, 'day')        ? 6 : effEvE.diff(weekStartDate, 'day');

    // Split the in-week segment into pre-ship / show / post-ship column counts.
    // When ship dates are hidden, preCols=postCols=0 and showCols=span (existing behavior).
    let preCols = 0, showCols = 0, postCols = 0;
    const showInWeek = !evS.isAfter(weekEnd, 'day') && !evE.isBefore(weekStartDate, 'day');
    if (showInWeek) {
      const showStart = evS.isBefore(weekStartDate, 'day') ? 0 : evS.diff(weekStartDate, 'day');
      const showEnd   = evE.isAfter(weekEnd, 'day')        ? 6 : evE.diff(weekStartDate, 'day');
      preCols  = showStart - startCol;
      showCols = showEnd - showStart + 1;
      postCols = endCol - showEnd;
    } else if (evS.isAfter(weekEnd, 'day')) {
      // Entire week falls in the ship-out window
      preCols = endCol - startCol + 1;
    } else {
      // Entire week falls in the ship-back window
      postCols = endCol - startCol + 1;
    }

    // Anchor weeks — the week that contains the actual ship_out_date or ship_back_date.
    // Used in the renderer to decide which ship-only weeks carry the "SHIP — name" label;
    // every other ship-only week shows just the dashed connector line.
    const isShipOutAnchorWeek  = !!(shipOut  && !shipOut.isBefore(weekStartDate,  'day') && !shipOut.isAfter(weekEnd,  'day'));
    const isShipBackAnchorWeek = !!(shipBack && !shipBack.isBefore(weekStartDate, 'day') && !shipBack.isAfter(weekEnd, 'day'));

    candidates.push({
      event,
      startCol,
      endCol,
      span: endCol - startCol + 1,
      isStart: !effEvS.isBefore(weekStartDate, 'day'),
      isEnd:   !effEvE.isAfter(weekEnd, 'day'),
      preCols,
      showCols,
      postCols,
      isShipOutAnchorWeek,
      isShipBackAnchorWeek,
    });
  });

  // Group by tour — tour events must share a lane. When tour lanes are off, treat each
  // tour event as standalone (no shared lane bg, no cross-week extension); chain icons
  // on individual bars still signal tour membership via tourSpans lookup in the
  // standalone-placement branch below.
  const tourGroups = {};  // tour_id → [candidates]
  const standalone = [];
  candidates.forEach(c => {
    if (c.event.tour_id && showTourLanes) {
      const tid = c.event.tour_id;
      if (!tourGroups[tid]) tourGroups[tid] = [];
      tourGroups[tid].push(c);
    } else {
      standalone.push(c);
    }
  });

  // Build placement list: tour groups treated as single wide blocks for lane assignment
  // Each tour group reserves a lane spanning from its earliest startCol to latest endCol.
  // If the tour has members in earlier/later weeks (per tourSpans), extend the lane
  // claim to the row edge so the cross-row connector reads as continuous.
  const tourBlocks = Object.entries(tourGroups).map(([tid, members]) => {
    members.sort((a, b) => a.startCol - b.startCol);
    const blockStart = Math.min(...members.map(m => m.startCol));
    const blockEnd   = Math.max(...members.map(m => m.endCol));
    const span       = tourSpans[tid];
    const hasEarlier = !!(span && span.minDate && span.minDate.isBefore(weekStartDate, 'day'));
    const hasLater   = !!(span && span.maxDate && span.maxDate.isAfter(weekEnd, 'day'));
    const laneStart  = hasEarlier ? 0 : blockStart;
    const laneEnd    = hasLater   ? 6 : blockEnd;
    return {
      tourId: tid,
      members,
      blockStart,
      blockEnd,
      laneStart,
      laneEnd,
      blockSpan: blockEnd - blockStart + 1,
      laneSpan:  laneEnd  - laneStart + 1,
      hasEarlier,
      hasLater,
      isPhantom: false,
    };
  });

  // Phantom tour blocks: weeks sandwiched between tour events but with no in-week members.
  // Renders the lane bg so the tour reads as continuous even when intermediate weeks
  // are empty. Skipped entirely when tour lanes are off.
  if (showEmptyTourWeeks && showTourLanes) {
    Object.entries(tourSpans).forEach(([tid, span]) => {
      if (tourGroups[tid]) return;                                                  // already has in-week members
      if (!span.minDate || !span.minDate.isBefore(weekStartDate, 'day')) return;    // tour hasn't started (or no visible members)
      if (!span.maxDate || !span.maxDate.isAfter(weekEnd, 'day'))      return;      // tour has finished (or no visible members)
      tourBlocks.push({
        tourId: tid,
        members: [],
        blockStart: 0,
        blockEnd: 6,
        laneStart: 0,
        laneEnd: 6,
        blockSpan: 7,
        laneSpan: 7,
        hasEarlier: true,
        hasLater: true,
        isPhantom: true,
      });
    });
  }

  // Merge tour blocks and standalone, sort by span (longest first).
  // Tours use their extended laneSpan/laneStart so cross-row tours get top priority
  // and their reserved lane covers the full visual extent.
  const allItems = [
    ...tourBlocks.map(b => ({ type: 'tour', ...b, span: b.laneSpan, startCol: b.laneStart })),
    ...standalone.map(c => ({ type: 'single', ...c, span: c.endCol - c.startCol + 1 })),
  ];
  allItems.sort((a, b) => b.span - a.span || a.startCol - b.startCol);

  // Greedy lane assignment
  const lanes = [];   // lanes[i] = highest occupied endCol
  const placed = [];
  const overflow = {};
  const tourConnectors = []; // gap segments between tour events

  allItems.forEach(item => {
    if (item.type === 'tour') {
      // Find a lane where the entire (possibly row-extended) block fits
      let lane = 0;
      while (lane < lanes.length && lanes[lane] >= item.laneStart) lane++;
      if (lane >= MAX_LANES) {
        item.members.forEach(c => {
          for (let col = c.startCol; col <= c.endCol; col++) overflow[col] = (overflow[col] || 0) + 1;
        });
        return;
      }
      if (lane === lanes.length) lanes.push(-1);
      lanes[lane] = item.laneEnd;
      // Tag each member with its GLOBAL position in the tour (first / last / middle).
      // Used by renderEventBar to position the chain icon — left for "continues from earlier",
      // right for "continues into later". Per-week ordering doesn't drive the icon.
      const span = tourSpans[item.tourId];
      item.members.forEach((c) => placed.push({
        ...c,
        lane,
        isTourFirstGlobal: !!(span && span.firstEventId === c.event.id),
        isTourLastGlobal:  !!(span && span.lastEventId  === c.event.id),
      }));
      // Full-span tour lane background — extended to row edge when the tour
      // continues into an adjacent week (flatLeft/flatRight drop the rounded cap).
      // Dashed-line gap connectors are intentionally NOT emitted — dashed lines are
      // now reserved for ship logistics; tour grouping is conveyed by the colored
      // lane bg alone.
      tourConnectors.push({
        startCol: item.laneStart,
        endCol:   item.laneEnd,
        lane,
        type: 'lane',
        tourId: item.tourId,
        flatLeft:  item.hasEarlier,
        flatRight: item.hasLater,
      });
    } else {
      // Standard single-event placement. Tour-position flags still computed here so a
      // tour event placed via the standalone path (when tour lanes are off) keeps its
      // chain icons.
      let lane = 0;
      while (lane < lanes.length && lanes[lane] >= item.startCol) lane++;
      if (lane >= MAX_LANES) {
        for (let col = item.startCol; col <= item.endCol; col++) overflow[col] = (overflow[col] || 0) + 1;
        return;
      }
      if (lane === lanes.length) lanes.push(-1);
      lanes[lane] = item.endCol;
      const span = item.event && item.event.tour_id ? tourSpans[item.event.tour_id] : null;
      placed.push({
        ...item,
        lane,
        isTourFirstGlobal: !!(span && span.firstEventId === item.event.id),
        isTourLastGlobal:  !!(span && span.lastEventId  === item.event.id),
      });
    }
  });

  return { placements: placed, overflowByCol: overflow, maxLane: lanes.length, tourConnectors };
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                           */
/* ------------------------------------------------------------------ */
const CalendarSkeleton = () => (
  <div className="rounded-xl overflow-hidden border border-gray-700/40 day:border-gray-200 bg-gray-900/40 day:bg-white animate-pulse">
    <div className="grid grid-cols-7 bg-surface-container-low day:bg-gray-50 border-b border-outline-variant day:border-gray-200">
      {DAYS_FULL.map(d => <div key={d} className="py-2.5 flex justify-center"><div className="w-6 h-2.5 rounded bg-gray-700/40 day:bg-gray-200" /></div>)}
    </div>
    {[...Array(5)].map((_, wi) => (
      <div key={wi} className="grid grid-cols-7 border-b border-outline-variant day:border-gray-200">
        {[...Array(7)].map((_, ci) => (
          <div key={ci} className={`min-h-[100px] lg:min-h-[116px] p-2 ${ci < 6 ? 'border-r border-outline-variant day:border-gray-200' : ''}`}>
            <div className="w-5 h-3 rounded bg-gray-700/30 day:bg-gray-200 mb-3" />
            {ci % 3 !== 2 && <div className="w-full h-[18px] rounded bg-gray-700/20 day:bg-gray-200/70 mb-1.5" />}
            {ci % 2 === 0 && <div className="w-3/4 h-[18px] rounded bg-gray-700/15 day:bg-gray-200/60" />}
          </div>
        ))}
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Day popover (on hover delay)                                       */
/* ------------------------------------------------------------------ */
const DayPopover = ({ dateStr, events, tourMap, rect, calendarRect }) => {
  if (!events.length) return null;

  const totalDrones = events.reduce((s, e) => s + buffered(e), 0);
  const showAbove = rect && calendarRect && (rect.top - calendarRect.top) > calendarRect.height / 2;

  // Group by tour
  const tours = {};
  const standalone = [];
  events.forEach(e => {
    if (e.tour_id) {
      if (!tours[e.tour_id]) tours[e.tour_id] = { name: tourMap[e.tour_id] || 'Tour', events: [] };
      tours[e.tour_id].events.push(e);
    } else {
      standalone.push(e);
    }
  });

  return (
    <div
      className={`absolute z-50 w-64 bg-gray-900/95 day:bg-white day:border-gray-200 backdrop-blur-md border border-gray-700/60 rounded-lg shadow-2xl shadow-black/40 day:shadow-gray-900/15 p-3 pointer-events-none ${showAbove ? 'bottom-full mb-2' : 'top-full mt-2'}`}
      style={{ left: '50%', transform: 'translateX(-50%)' }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs font-semibold text-white day:text-gray-900">{dayjs(dateStr).format('ddd, MMM D')}</span>
        <span className="text-[10px] text-on-surface-variant day:text-gray-500 tabular-nums">{totalDrones.toLocaleString()} drones</span>
      </div>
      <div className="space-y-1">
        {standalone.map(e => {
          const sc = STATUS[getDisplayStatus(e)] || STATUS.pending;
          return (
            <div key={e.id} className="flex items-center gap-2 text-[11px]">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
              <span className={`truncate flex-1 ${sc.text}`}>{e.name}</span>
              <span className="text-on-surface-variant day:text-gray-500 tabular-nums flex-shrink-0">{buffered(e)}</span>
            </div>
          );
        })}
        {Object.values(tours).map((tour, ti) => (
          <div key={ti}>
            <div className="flex items-center gap-1.5 mt-1">
              <ChainIcon className="w-3 h-3 text-cyan-500/70 day:text-cyan-600 flex-shrink-0" />
              <span className="text-[10px] text-cyan-300 day:text-cyan-700 font-medium uppercase tracking-wider">{tour.name}</span>
            </div>
            {tour.events.map(e => {
              const sc = STATUS[getDisplayStatus(e)] || STATUS.pending;
              return (
                <div key={e.id} className="flex items-center gap-2 text-[11px] pl-4">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                  <span className={`truncate flex-1 ${sc.text}`}>{e.name}</span>
                  <span className="text-on-surface-variant day:text-gray-500 tabular-nums flex-shrink-0">{buffered(e)}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="text-[9px] text-on-surface-variant day:text-gray-500 mt-2 text-center">Click for availability details</div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Mini month preview (shown on nav hover)                            */
/* ------------------------------------------------------------------ */
const getPreviewHeat = (drones) => {
  if (drones >= 500) return 'bg-red-500/40 day:bg-red-200 text-red-300/80 day:text-red-700';
  if (drones >= 200) return 'bg-amber-500/35 day:bg-amber-200 text-amber-300/80 day:text-amber-700';
  if (drones > 0)    return 'bg-sky-500/30 day:bg-sky-200 text-sky-300/80 day:text-sky-700';
  return null;
};

const MiniMonthPreview = ({ month, eventDayMap }) => {
  const startDay = month.startOf('month').day();
  const daysInMonth = month.daysInMonth();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventCount = eventDayMap.size;

  return (
    <div className="bg-gray-900/95 day:bg-white backdrop-blur-md border border-gray-700/60 day:border-gray-200 rounded-lg shadow-2xl shadow-black/40 day:shadow-gray-900/15 p-3 pointer-events-none">
      <div className="text-[10px] text-on-surface-variant day:text-gray-700 font-semibold text-center mb-2">{month.format('MMM YYYY')}</div>
      <div className="grid grid-cols-7 gap-[3px]" style={{ width: '112px' }}>
        {cells.map((d, i) => {
          const drones = d && eventDayMap.get(d);
          const heatCls = drones ? getPreviewHeat(drones) : null;
          return (
            <div key={i} className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center text-[7px] ${
              d === null ? '' : heatCls ? heatCls : 'bg-surface-container-low day:bg-gray-100 text-on-surface-disabled day:text-gray-500'
            }`}>
              {d}
            </div>
          );
        })}
      </div>
      {eventCount > 0 && (
        <div className="text-[9px] text-on-surface-variant day:text-gray-500 text-center mt-1.5">{eventCount} day{eventCount !== 1 ? 's' : ''} with events</div>
      )}
    </div>
  );
};

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
const EventCalendar = () => {
  const api = useApi();
  const dayMode = useThemeStore((s) => s.dayMode);

  /* state */
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState(() => {
    const prefs = loadCalendarPrefs();
    return Array.isArray(prefs?.activeStatuses) ? new Set(prefs.activeStatuses) : DEFAULT_ACTIVE;
  });
  const [tourMap, setTourMap] = useState({});
  const [equalRows, setEqualRows] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.equalRows === 'boolean' ? prefs.equalRows : true;
  });
  const [showRentals, setShowRentals] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.showRentals === 'boolean' ? prefs.showRentals : true;
  });
  const [rentals, setRentals] = useState([]);
  const [showLeases, setShowLeases] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.showLeases === 'boolean' ? prefs.showLeases : false;
  });
  const [showEmptyTourWeeks, setShowEmptyTourWeeks] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.showEmptyTourWeeks === 'boolean' ? prefs.showEmptyTourWeeks : false;
  });
  const [showShipDates, setShowShipDates] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.showShipDates === 'boolean' ? prefs.showShipDates : false;
  });
  const [showTourLanes, setShowTourLanes] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.showTourLanes === 'boolean' ? prefs.showTourLanes : true;
  });
  // "Only shows that still need airspace work." Not persisted as ON by default —
  // it is a working filter, and a calendar that silently hides most of the month
  // on load is a support ticket.
  const [airspaceOnly, setAirspaceOnly] = useState(() => {
    const prefs = loadCalendarPrefs();
    return typeof prefs?.airspaceOnly === 'boolean' ? prefs.airspaceOnly : false;
  });

  /* Persist view/filter prefs */
  useEffect(() => {
    try {
      localStorage.setItem(
        CALENDAR_PREFS_KEY,
        JSON.stringify({
          activeStatuses: Array.from(activeStatuses),
          equalRows,
          showRentals,
          showLeases,
          showEmptyTourWeeks,
          showShipDates,
          airspaceOnly,
          showTourLanes,
        }),
      );
    } catch {
      /* quota exceeded or storage disabled — preferences won't persist this session */
    }
  }, [activeStatuses, equalRows, showRentals, showLeases, showEmptyTourWeeks, showShipDates, showTourLanes, airspaceOnly]);
  const [leases, setLeases] = useState([]);
  const [highlightEventId, setHighlightEventId] = useState(null);   // multi-day event hover
  const [highlightTourId, setHighlightTourId] = useState(null);     // tour group hover
  const [hoverCard, setHoverCard] = useState(null);                 // { event, x, y } | null — portal hover card
  const [popup, setPopup] = useState(null);                         // { event, anchorRect, triggerEl } | null — click popup


  /* hover/popover */
  const [popover, setPopover] = useState(null);             // { day, rect }
  const popoverTimer = useRef(null);
  const calendarRef = useRef(null);
  const cellRefs = useRef({});
  // Debounce bar highlight/dim state so quick fly-bys across the grid don't flicker.
  // Enter intent applies after 80ms; leave clears after 150ms (small overlap absorbs
  // rapid bar-to-bar moves without a momentary "no highlight" gap).
  const hoverIntentTimer = useRef(null);

  // Clean up timers on unmount
  useEffect(() => () => {
    clearTimeout(popoverTimer.current);
    clearTimeout(hoverIntentTimer.current);
  }, []);

  /* mini month preview */
  const [previewSide, setPreviewSide] = useState(null);     // 'prev' | 'next'
  const adjacentCache = useRef({});

  /* measure week row height for overflow detection in equal mode */
  const [rowHeight, setRowHeight] = useState(0);
  const firstRowRef = useRef(null);

  /* ── Data fetching ── */
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        // Pad by SHIP_DATE_MAX_DAYS so events whose ship_out/ship_back falls in the
        // visible month — but whose event_date is in an adjacent month — still load.
        const start = currentMonth.startOf('month').subtract(SHIP_DATE_MAX_DAYS, 'day').format('YYYY-MM-DD');
        const end   = currentMonth.endOf('month').add(SHIP_DATE_MAX_DAYS, 'day').format('YYYY-MM-DD');
        const [eventsData, rentalsData, leasesData] = await Promise.all([
          api.get(`/events/calendar?start=${start}&end=${end}`),
          api.get(`/orders/rental-calendar?start=${start}&end=${end}`).catch(() => []),
          api.get(`/orders/lease-calendar?start=${start}&end=${end}`).catch(() => []),
        ]);
        setEvents(eventsData || []);
        // Normalize rentals into event-like shape for the calendar layout engine
        setRentals((rentalsData || []).map(r => ({
          ...r,
          _isRental: true,
          event_date: r.effective_date,
          end_date: r.end_date,
          name: `${r.customer_name_resolved || r.customer_name || 'Rental'} — ${r.so_number || 'Rental'}`,
          ls_drone_count: 0,
          pyro_drone_count: 0,
          buffer_percentage: 0,
          status: r.status === 'delivered' ? 'active' : r.status,
        })));
        // Normalize leases into event-like shape
        setLeases((leasesData || []).map(l => ({
          ...l,
          _isLease: true,
          event_date: l.effective_date,
          end_date: l.end_date || end, // permanent leases: cap at visible range end
          _isPermanent: !l.end_date,    // preserve original "no end date" signal for the click popup
          name: `${l.customer_name_resolved || l.customer_name || 'Lease'} — ${l.so_number || 'Lease'}`,
          ls_drone_count: 0,
          pyro_drone_count: 0,
          buffer_percentage: 0,
          status: l.status === 'delivered' ? 'active' : l.status,
        })));
      } catch (error) {
        console.error('Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();

    // Prefetch adjacent months for preview — store Map<dayNum, totalBufferedDrones>
    const prefetch = async (m) => {
      const key = m.format('YYYY-MM');
      if (adjacentCache.current[key]) return;
      try {
        const data = await api.get(`/events/calendar?start=${m.startOf('month').format('YYYY-MM-DD')}&end=${m.endOf('month').format('YYYY-MM-DD')}`, { silent: true });
        const dayMap = new Map(); // day -> total buffered drones
        (data || []).forEach(e => {
          const s = dayjs(e.event_date);
          const ed = e.end_date ? dayjs(e.end_date) : s;
          const b = buffered(e);
          let d = s;
          while (d.isBefore(ed.add(1, 'day'), 'day')) {
            if (d.month() === m.month() && d.year() === m.year()) {
              const dn = d.date();
              dayMap.set(dn, (dayMap.get(dn) || 0) + b);
            }
            d = d.add(1, 'day');
          }
        });
        adjacentCache.current[key] = dayMap;
      } catch { /* silent */ }
    };
    prefetch(currentMonth.subtract(1, 'month'));
    prefetch(currentMonth.add(1, 'month'));
  }, [currentMonth]);

  useEffect(() => {
    api.get('/tours', { silent: true })
      .then(data => {
        const map = {};
        (data || []).forEach(t => { map[t.id] = t.name; });
        setTourMap(map);
      })
      .catch(() => {});
  }, []);

  // Close click-popup when navigating months — anchor rect would be stale.
  useEffect(() => { setPopup(null); }, [currentMonth]);

  const prevMonth = () => setCurrentMonth(m => m.subtract(1, 'month'));
  const nextMonth = () => setCurrentMonth(m => m.add(1, 'month'));

  const goToday = () => setCurrentMonth(dayjs().startOf('month'));

  /* ── Popover handlers ── */
  const startPopover = (day) => {
    clearTimeout(popoverTimer.current);
    popoverTimer.current = setTimeout(() => {
      const el = cellRefs.current[day];
      const cal = calendarRef.current;
      if (el && cal) {
        setPopover({ day, rect: el.getBoundingClientRect(), calendarRect: cal.getBoundingClientRect() });
      }
    }, 400);
  };

  const cancelPopover = () => {
    clearTimeout(popoverTimer.current);
    setPopover(null);
  };

  /* ── Computed ── */
  const today = dayjs();
  const isCurrentMonth = today.month() === currentMonth.month() && today.year() === currentMonth.year();
  const startDay = currentMonth.startOf('month').day();
  const daysInMonth = currentMonth.daysInMonth();
  const totalCells = Math.ceil((startDay + daysInMonth) / 7) * 7;

  const cells = useMemo(() => {
    const c = [];
    const prevEnd = currentMonth.subtract(1, 'month').daysInMonth();
    for (let i = startDay - 1; i >= 0; i--) c.push({ day: prevEnd - i, outside: true });
    for (let d = 1; d <= daysInMonth; d++) c.push({ day: d, outside: false });
    for (let i = 1; c.length < totalCells; i++) c.push({ day: i, outside: true });
    return c;
  }, [currentMonth, startDay, daysInMonth, totalCells]);

  const weekRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [cells]);

  // Measure row height for overflow detection in equal mode
  useEffect(() => {
    if (!equalRows || !firstRowRef.current) return;
    const measure = () => setRowHeight(firstRowRef.current?.offsetHeight || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(firstRowRef.current);
    return () => ro.disconnect();
  }, [equalRows, loading, weekRows.length]);

  // Combined events + rentals + leases for layout
  const allItems = useMemo(() => {
    // Airspace filter applies to EVENTS ONLY. Rentals and leases have no airspace
    // workflow, so including them would make "show me what needs airspace" return
    // a pile of rows that can never need it.
    const base = airspaceOnly
      ? events.filter(e => e.airspace_status && e.airspace_status !== 'complete')
      : events;
    const combined = [...base];
    if (!airspaceOnly && showRentals) combined.push(...rentals);
    if (!airspaceOnly && showLeases) combined.push(...leases);
    return combined;
  }, [events, rentals, showRentals, leases, showLeases, airspaceOnly]);

  // Events grouped by day (current month only)
  const eventsByDate = useMemo(() => {
    const map = {};
    allItems.forEach(event => {
      const s = dayjs(event.event_date);
      const e = event.end_date ? dayjs(event.end_date) : s;
      let d = s;
      while (d.isBefore(e.add(1, 'day'), 'day')) {
        if (d.month() === currentMonth.month() && d.year() === currentMonth.year()) {
          const key = d.date();
          if (!map[key]) map[key] = [];
          map[key].push(event);
        }
        d = d.add(1, 'day');
      }
    });
    return map;
  }, [allItems, currentMonth]);

  // Per-week lane layouts
  const weekLayouts = useMemo(() => {
    const tourSpans = buildTourSpans(allItems, activeStatuses);
    const opts = { showEmptyTourWeeks, showShipDates, showTourLanes };
    return weekRows.map((_week, wi) => {
      const weekStartDate = currentMonth.startOf('month').subtract(startDay, 'day').add(wi * 7, 'day');
      return computeWeekLayout(weekStartDate, allItems, activeStatuses, tourSpans, opts);
    });
  }, [weekRows, allItems, activeStatuses, currentMonth, startDay, showEmptyTourWeeks, showShipDates, showTourLanes]);

  // Month stats

  const monthStats = useMemo(() => {
    const unique = new Set();
    let drones = 0, pending = 0, confirmed = 0, tours = new Set();
    events.forEach(e => {
      if (unique.has(e.id)) return;
      unique.add(e.id);
      drones += (e.ls_drone_count || 0) + (e.pyro_drone_count || 0);
      if (e.status === 'pending') pending++;
      if (e.status === 'confirmed') confirmed++;
      if (e.tour_id) tours.add(e.tour_id);
    });
    return { total: unique.size, drones, pending, confirmed, tours: tours.size };
  }, [events]);

  const toggleStatus = (s) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  };

  /* ── Render helpers ── */
  const renderEventBar = (p, animIdx) => {
    const {
      event, startCol, endCol, isStart, isEnd, lane,
      isTourFirstGlobal = false, isTourLastGlobal = false,
      preCols = 0, showCols = (endCol - startCol + 1), postCols = 0,
      isShipOutAnchorWeek = false, isShipBackAnchorWeek = false,
    } = p;
    const isRental = !!event._isRental;
    const isLease = !!event._isLease;
    const displayStatus = getDisplayStatus(event);
    const sc = isLease ? LEASE_STATUS : isRental ? RENTAL_STATUS : (STATUS[displayStatus] || STATUS.pending);
    const isTour = !isRental && !isLease && !!event.tour_id;
    // Wing labels: a drive when a truck is assigned or the mode says driving,
    // otherwise SHIP/RECEIVE. Same rule as the event page's Leaves / Returns, so
    // the two never label one ship_out_date differently.
    const isDriving = drivesOut((event.vehicles || []).length > 0, event.transport_mode);
    const outLabel = isDriving ? 'DRIVE'  : 'SHIP';
    const backLabel = isDriving ? 'RETURN' : 'RECEIVE';
    // Per-event/tour accent. Leases, rentals, and cancelled events stay status-only so the
    // red "cancelled" signal isn't overridden by the per-tour color.
    const accent = (isRental || isLease || displayStatus === 'cancelled') ? null : accentForEvent(event, dayMode);
    // Chain icon positioning by global tour position:
    //   First event  → chain on right  (continues into later weeks)
    //   Last event   → chain on left   (continues from earlier weeks)
    //   Middle event → chains on both sides
    //   Single event → no chains (shouldn't happen with a real tour but guarded anyway)
    const isOnlyTourEvent = isTour && isTourFirstGlobal && isTourLastGlobal;
    const showChainLeft  = isTour && !isOnlyTourEvent && !isTourFirstGlobal;
    const showChainRight = isTour && !isOnlyTourEvent && !isTourLastGlobal;

    // Is this bar highlighted via hover on a related bar?
    const isHighlighted =
      (highlightEventId && highlightEventId === event.id) ||
      (highlightTourId && highlightTourId === event.tour_id);

    // Is something else highlighted (dim non-highlighted bars)?
    const somethingHighlighted = highlightEventId || highlightTourId;
    const isDimmed = somethingHighlighted && !isHighlighted;

    const handleEnter = (e) => {
      // Hover card pops immediately. Dim/highlight engages on a half-second hover-intent
      // so casual mousing doesn't trigger the dim, then fades slowly via transition class.
      setHoverCard({ event, x: e.clientX, y: e.clientY });
      if (hoverIntentTimer.current) clearTimeout(hoverIntentTimer.current);
      hoverIntentTimer.current = setTimeout(() => {
        if (isTour) {
          setHighlightTourId(event.tour_id);
          setHighlightEventId(null);
        } else {
          setHighlightEventId(event.id);
          setHighlightTourId(null);
        }
      }, 500);
    };
    const handleMove = (e) => {
      setHoverCard(prev => (prev && prev.event.id === event.id ? { ...prev, x: e.clientX, y: e.clientY } : prev));
    };
    const handleLeave = () => {
      // Leave is intentionally quick — the user has signaled they're done with this bar.
      setHoverCard(null);
      if (hoverIntentTimer.current) clearTimeout(hoverIntentTimer.current);
      hoverIntentTimer.current = setTimeout(() => {
        setHighlightTourId(null);
        setHighlightEventId(null);
      }, 80);
    };

    const openPopup = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const barEl = e.currentTarget.closest('[data-cal-bar]');
      if (!barEl) return;
      setHoverCard(null);
      setHighlightTourId(null);
      setHighlightEventId(null);
      setPopup({
        event,
        clickX: e.clientX,
        clickY: e.clientY,
        triggerEl: barEl,
      });
    };

    const NameButton = (
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={popup?.event?.id === event.id}
        aria-controls={`event-popup-${event.id}`}
        onClick={openPopup}
        className="truncate text-left hover:underline decoration-white/20 day:decoration-gray-900/30 cursor-pointer"
        style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 }}
      >
        {isLease && <span className="opacity-60 mr-1">L</span>}
        {isRental && <span className="opacity-60 mr-1">R</span>}
        {event.name}
      </button>
    );

    // Show the SHIP — name label only on the anchor week (the one containing the actual
    // ship_out_date / ship_back_date). Every other ship-only week is just a dashed line.
    const labelInPre  = preCols  > 0 && isShipOutAnchorWeek;
    const labelInPost = postCols > 0 && isShipBackAnchorWeek;

    // Ship-out wing — dashed connector line (gear in transit TO the event).
    // Anchored on the ship_out_date week, where the italic SHIP — name label sits
    // inline with the dashed line.
    const ShipOutWing = (
      <>
        {labelInPre && (
          <button
            type="button"
            aria-haspopup="true"
            aria-expanded={popup?.event?.id === event.id}
            aria-controls={`event-popup-${event.id}`}
            onClick={openPopup}
            className="truncate italic opacity-70 hover:underline decoration-white/20 day:decoration-gray-900/30 cursor-pointer flex-shrink min-w-0 mr-1"
          >
            {outLabel} — {event.name}
          </button>
        )}
        <div className="flex-1 border-t border-dashed border-current opacity-40" />
      </>
    );

    // Receive wing — post-show window. Three modes:
    //   Anchor week + showCols > 0: blank gap + RECEIVE label (same-week receive)
    //   Anchor week + showCols === 0: leading dashed line + RECEIVE label (receive-only row)
    //   Non-anchor with postCols > 0: dashed line spanning the wing — covers both the
    //     event week's post tail (transit continues to next row) AND intermediate weeks
    //     between event and receive. Keeps the transit visual continuous across rows.
    const ReceiveWing = labelInPost ? (
      <>
        {postCols > 1 && (
          <div className="flex items-center min-w-0" style={{ gridColumn: `1 / ${postCols}` }}>
            <div className="w-full border-t border-dashed border-current opacity-40" />
          </div>
        )}
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={popup?.event?.id === event.id}
          aria-controls={`event-popup-${event.id}`}
          onClick={openPopup}
          className="truncate text-left italic opacity-70 hover:underline decoration-white/20 day:decoration-gray-900/30 cursor-pointer pl-1.5 pr-1 min-w-0"
          style={{ gridColumn: `${postCols} / ${postCols + 1}` }}
        >
          {backLabel} — {event.name}
        </button>
      </>
    ) : (
      <div className="flex items-center min-w-0 px-1" style={{ gridColumn: `1 / ${postCols + 1}` }}>
        <div className="w-full border-t border-dashed border-current opacity-40" />
      </div>
    );

    // Each segment occupies a fixed number of CALENDAR columns within the bar. Using a
    // CSS grid with `minmax(0, 1fr)` columns guarantees each segment matches its allocated
    // column count regardless of content width — flex was letting wide content (e.g.,
    // "RECEIVE — Event Name" buttons) push siblings sideways into adjacent date cells.
    const totalCols = preCols + showCols + postCols;
    // Defensive: gridTemplateColumns: repeat(0, ...) silently breaks layout. Skip the bar.
    if (totalCols === 0) return null;
    let cursor = 1;
    const preStart  = cursor;                  cursor += preCols;
    const showStart = cursor;                  cursor += showCols;
    const postStart = cursor;                  // ends at totalCols+1

    return (
      <div
        key={`${event.id}-L${lane}`}
        data-cal-bar
        onMouseEnter={handleEnter}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className={`
          h-[20px] lg:h-[22px] grid overflow-hidden
          ${isStart ? 'ml-[3px]' : ''}
          ${isEnd   ? 'mr-[3px]' : ''}
          ${!accent ? sc.text : ''}
          ${isHighlighted ? 'brightness-125 day:brightness-95 scale-y-110 z-10' : ''}
          ${isDimmed ? 'opacity-40' : ''}
          ${!somethingHighlighted ? 'hover:brightness-125 day:hover:brightness-95' : ''}
          cal-bar-enter cal-bar-fade
        `}
        style={{
          gridColumn: `${startCol + 1} / ${endCol + 2}`,
          gridRow: lane + 1,
          gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`,
          animationDelay: `${animIdx * 30}ms`,
          ...(accent && { color: accent.text }),
        }}
      >
        {preCols > 0 && (
          <div
            className={`flex items-center min-w-0 text-[9px] lg:text-[10px] leading-none ${labelInPre ? 'px-1.5' : 'px-1'}`}
            style={{ gridColumn: `${preStart} / ${preStart + preCols}` }}
          >
            {ShipOutWing}
          </div>
        )}
        {showCols > 0 && (
          <div
            className={`${sc.bar} flex items-center min-w-0 ring-1 ring-inset rounded-[4px] text-[9px] lg:text-[10px] leading-none pl-1.5 pr-1
              ${!accent ? sc.ring : ''}
              ${isHighlighted ? 'ring-white/20 day:ring-gray-900/30' : ''}
              ${!somethingHighlighted ? 'hover:ring-white/10 day:hover:ring-gray-900/20' : ''}
            `}
            style={{
              gridColumn: `${showStart} / ${showStart + showCols}`,
              ...(accent && { '--tw-ring-color': accent.border }),
            }}
          >
            {showChainLeft  && <ChainIcon className="w-3 h-3 mr-1 flex-shrink-0" />}
            {event.pyro_drone_count > 0 && (
              <FlameIcon className="w-3 h-3 mr-1 flex-shrink-0" title="Pyro show" />
            )}
            {/* Airspace + travel-note marks ride LEFT of the name, where the eye
                already goes for the pyro flame. Both are absent in the ordinary
                case, so a clean row stays clean. */}
            {(() => {
              const mark = airspaceMark(event.airspace_status);
              return mark ? (
                <span
                  className={`mr-1 flex-shrink-0 font-bold leading-none ${mark.cls}`}
                  title={mark.label}
                  aria-label={mark.label}
                >
                  {mark.glyph}
                </span>
              ) : null;
            })()}
            {travelNoteText(event) && (
              <span
                className="mr-1 flex-shrink-0 opacity-70 leading-none"
                title={travelNoteText(event)}
                aria-label="Has travel notes"
              >
                &#9998;
              </span>
            )}
            {NameButton}
            {/* The assigned truck. She runs the production meeting off this view and
                the one thing it never said was which vehicle is going. Sits before
                the show time so the times still form a readable right-hand column,
                and truncates rather than pushing them out of line. */}
            {vehicleLabel(event) && (
              <span
                className="ml-1.5 flex-shrink min-w-0 truncate opacity-70"
                title={`Vehicle: ${(event.vehicles || []).map(v => v.name || v.display_code).join(', ')}`}
              >
                {vehicleLabel(event)}
              </span>
            )}
            {/* Show time, right-aligned so it lands in the same place on every bar
                and a column of them can be read down. Second show time appended for
                doubles — it is informational, and a producer scanning for call
                times needs both. */}
            {compactTime(event.show_time) && (
              <span className="ml-1 flex-shrink-0 tabular-nums opacity-80">
                {compactTime(event.show_time)}
                {event.is_double_show && event.second_show_time
                  ? ` / ${compactTime(event.second_show_time)}`
                  : ''}
              </span>
            )}
            {showChainRight && <ChainIcon className="w-3 h-3 ml-1 flex-shrink-0" />}
          </div>
        )}
        {postCols > 0 && (
          <div
            className="grid items-center min-w-0 text-[9px] lg:text-[10px] leading-none"
            style={{
              gridColumn: `${postStart} / ${postStart + postCols}`,
              gridTemplateColumns: `repeat(${postCols}, minmax(0, 1fr))`,
            }}
          >
            {ReceiveWing}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout width="full" kind="workspace" className={`pt-4 flex flex-col min-h-page ${dayMode ? 'day-mode bg-gray-50 text-gray-900' : ''}`}>
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white day:text-gray-900 tracking-tight">Calendar</h1>
            <div className="h-5 w-px bg-gray-700/60 day:bg-gray-300" />
            <Link to="/events" className="text-xs text-on-surface-variant day:text-gray-600 hover:text-on-surface day:hover:text-gray-900 transition-colors flex items-center gap-1.5 group">
              <svg className="w-3.5 h-3.5 text-on-surface-disabled day:text-gray-400 group-hover:text-on-surface-variant day:group-hover:text-gray-700 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List
            </Link>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-on-surface-variant day:text-gray-600"><span className="text-on-surface day:text-gray-900 font-medium">{monthStats.total}</span> show{monthStats.total !== 1 ? 's' : ''}</span>
            <span className="text-gray-700 day:text-gray-300">/</span>
            <span className="text-on-surface-variant day:text-gray-600"><span className="text-on-surface day:text-gray-900 font-medium tabular-nums">{monthStats.drones.toLocaleString()}</span> drones</span>
            <Appear show={monthStats.tours > 0} className="inline-flex items-center gap-3">
              <span className="text-gray-700 day:text-gray-300">/</span>
              <span className="text-cyan-300 day:text-cyan-700"><span className="font-medium">{monthStats.tours}</span> tour{monthStats.tours !== 1 ? 's' : ''}</span>
            </Appear>
            <Appear show={showRentals && rentals.length > 0} className="inline-flex items-center gap-3">
              <span className="text-gray-700 day:text-gray-300">/</span>
              <span className="text-orange-300 day:text-orange-700"><span className="font-medium">{rentals.length}</span> rental{rentals.length !== 1 ? 's' : ''}</span>
            </Appear>
            <Appear show={showLeases && leases.length > 0} className="inline-flex items-center gap-3">
              <span className="text-gray-700 day:text-gray-300">/</span>
              <span className="text-blue-300 day:text-blue-700"><span className="font-medium">{leases.length}</span> lease{leases.length !== 1 ? 's' : ''}</span>
            </Appear>
          </div>
        </div>

        {/* Month nav */}
        <div className="relative flex flex-col items-end"
          onMouseLeave={() => setPreviewSide(null)}
        >
          <div className="flex items-center gap-1">
            <Tooltip label="Previous month"><IconButton
              label="Previous month"
              onClick={prevMonth}
              onMouseEnter={() => setPreviewSide('prev')}
            >
              <IconChevronLeft />
            </IconButton></Tooltip>

            <button
              onClick={goToday}
              className={`${buttonClass(isCurrentMonth ? 'tonal' : 'quiet', 'md')} min-w-[160px]`}
            >
              {currentMonth.format('MMMM YYYY')}
            </button>

            <Tooltip label="Next month"><IconButton
              label="Next month"
              onClick={nextMonth}
              onMouseEnter={() => setPreviewSide('next')}
            >
              <IconChevronRight />
            </IconButton></Tooltip>

            <div className="w-px h-5 bg-gray-700/60 day:bg-gray-300 mx-1" />
            <DayModeToggle />
          </div>

          {/* Mini month preview dropdown — absolutely positioned so it doesn't push content */}
          {previewSide && (() => {
            const previewMonth = previewSide === 'prev' ? currentMonth.subtract(1, 'month') : currentMonth.add(1, 'month');
            const cached = adjacentCache.current[previewMonth.format('YYYY-MM')];
            if (!cached) return null;
            return (
              <div className={`absolute top-full mt-2 z-50 ${previewSide === 'prev' ? 'right-1/2' : 'right-0'}`}>
                <MiniMonthPreview month={previewMonth} eventDayMap={cached} />
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Status filter chips ── */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {ALL_STATUSES.map(s => {
          const sc = STATUS[s];
          const active = activeStatuses.has(s);
          const count = events.filter(e => getDisplayStatus(e) === s).length;
          return (
            <button
              key={s}
              onClick={() => toggleStatus(s)}
              className={`
                px-2.5 py-1 rounded-full text-[10px] font-medium capitalize transition-all duration-150 ring-1 ring-inset
                ${active
                  ? `${sc.bar} ${sc.text} ${sc.ring} shadow-sm`
                  : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
                }
              `}
            >
              {s}
              {count > 0 && <span className="ml-1 tabular-nums opacity-60">{count}</span>}
            </button>
          );
        })}
        <div className="w-px h-4 bg-gray-700/40 day:bg-gray-300 mx-1" />
        <Tooltip label={airspaceOnly ? 'Rentals have no airspace workflow: turn off "Airspace to do" to see them' : undefined}><button
          onClick={() => setShowRentals(v => !v)}
          disabled={airspaceOnly}
          className={`
            px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-150 ring-1 ring-inset
            ${airspaceOnly ? 'opacity-50 cursor-not-allowed ' : ''}
            ${showRentals
              ? 'bg-orange-400/20 day:bg-orange-100 text-orange-300 day:text-orange-700 ring-orange-400/25 day:ring-orange-300 shadow-sm'
              : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
            }
          `}
        >
          Rentals{rentals.length > 0 && <span className="ml-1 tabular-nums opacity-60">{rentals.length}</span>}
        </button></Tooltip>
        <Tooltip label={airspaceOnly ? 'Leases have no airspace workflow: turn off "Airspace to do" to see them' : undefined}><button
          onClick={() => setShowLeases(v => !v)}
          disabled={airspaceOnly}
          className={`
            px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-150 ring-1 ring-inset
            ${airspaceOnly ? 'opacity-50 cursor-not-allowed ' : ''}
            ${showLeases
              ? 'bg-blue-400/20 day:bg-blue-100 text-blue-300 day:text-blue-700 ring-blue-400/25 day:ring-blue-300 shadow-sm'
              : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
            }
          `}
        >
          Leases{leases.length > 0 && <span className="ml-1 tabular-nums opacity-60">{leases.length}</span>}
        </button></Tooltip>
        <div className="w-px h-4 bg-gray-700/40 day:bg-gray-300 mx-1" />
        <Tooltip label={showTourLanes ? 'Hide tour lane backgrounds' : 'Show tour lane backgrounds'}><button
          onClick={() => setShowTourLanes(v => !v)}
          className={`
            inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-150 ring-1 ring-inset
            ${showTourLanes
              ? 'bg-cyan-400/20 day:bg-cyan-100 text-cyan-300 day:text-cyan-700 ring-cyan-400/25 day:ring-cyan-300 shadow-sm'
              : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
            }
          `}
        >
          <ChainIcon className="w-3 h-3" />
          Tour lanes
        </button></Tooltip>
        <Tooltip label={!showTourLanes ? 'Enable Tour lanes to use this' : (showEmptyTourWeeks ? 'Hide tour bridge on empty weeks' : 'Show tour bridge on weeks with no events')}><button
          onClick={() => setShowEmptyTourWeeks(v => !v)}
          disabled={!showTourLanes}
          className={`
            inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-150 ring-1 ring-inset
            ${!showTourLanes
              ? 'bg-gray-900/20 day:bg-gray-100/60 text-gray-700 day:text-gray-400 ring-gray-800/40 day:ring-gray-200 cursor-not-allowed opacity-50'
              : showEmptyTourWeeks
                ? 'bg-cyan-400/20 day:bg-cyan-100 text-cyan-300 day:text-cyan-700 ring-cyan-400/25 day:ring-cyan-300 shadow-sm'
                : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
            }
          `}
        >
          Empty tour weeks
        </button></Tooltip>
        <Tooltip label={airspaceOnly
            ? 'Show every show again'
            : 'Show only shows whose airspace is not complete'}><button
          onClick={() => setAirspaceOnly(v => !v)}
          className={`
            inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-150 ring-1 ring-inset
            ${airspaceOnly
              ? 'bg-amber-400/20 day:bg-amber-100 text-amber-300 day:text-amber-700 ring-amber-400/25 day:ring-amber-300 shadow-sm'
              : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
            }
          `}
        >
          Airspace to do
        </button></Tooltip>
        <Tooltip label={showShipDates ? 'Hide ship-out/ship-back days' : 'Show ship-out/ship-back days on event bars'}><button
          onClick={() => setShowShipDates(v => !v)}
          className={`
            inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-150 ring-1 ring-inset
            ${showShipDates
              ? 'bg-violet-400/20 day:bg-violet-100 text-violet-300 day:text-violet-700 ring-violet-400/25 day:ring-violet-300 shadow-sm'
              : 'bg-gray-900/40 day:bg-white text-on-surface-variant day:text-gray-500 ring-gray-800 day:ring-gray-200 hover:text-on-surface day:hover:text-gray-800 hover:ring-gray-700 day:hover:ring-gray-300'
            }
          `}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h13l5 5v5h-2a3 3 0 11-6 0H9a3 3 0 11-6 0V7z" />
          </svg>
          Ship dates
        </button></Tooltip>
        <div className="flex-1" />
        <Tooltip label={equalRows ? 'Switch to adaptive row heights' : 'Switch to equal row heights'}><button
          onClick={() => setEqualRows(v => !v)}
          className="flex items-center gap-1.5 text-[10px] text-on-surface-variant day:text-gray-600 hover:text-on-surface day:hover:text-gray-900 transition-colors group"
        >
          <svg className="w-3.5 h-3.5 text-on-surface-disabled day:text-gray-400 group-hover:text-on-surface-variant day:group-hover:text-gray-700 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {equalRows ? (
              <>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M3 12h18M3 18h18" />
              </>
            ) : (
              <>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M3 10h18M3 18h18" />
              </>
            )}
          </svg>
          {equalRows ? 'Equal rows' : 'Fit content'}
        </button></Tooltip>
      </div>

      {/* ── Calendar grid ── */}
      {loading ? <CalendarSkeleton /> : (
        <FadeIn className="flex-1 flex flex-col min-h-0">
        <div
          ref={calendarRef}
          className="rounded-xl overflow-hidden border border-gray-700/40 day:border-gray-200 bg-gray-900/40 day:bg-white backdrop-blur-sm flex-1 flex flex-col"
        >
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 bg-surface-container-low day:bg-gray-50 border-b border-outline-variant day:border-gray-200 flex-shrink-0">
            {DAYS_FULL.map((day, i) => (
              <div key={day} className={`py-2.5 text-center text-[10px] uppercase tracking-[0.15em] font-semibold select-none ${i === 0 || i === 6 ? 'text-on-surface-disabled day:text-gray-400' : 'text-on-surface-variant day:text-gray-600'}`}>
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{DAYS_SHORT[i]}</span>
              </div>
            ))}
          </div>

          {/* Week rows */}
          <div className="flex-1 flex flex-col">
          {weekRows.map((week, wi) => {
            const layout = weekLayouts[wi];
            const rowLanes = Math.max(layout.maxLane, 0);
            const eventAreaHeight = rowLanes * 24; // 22px bar + 2px gap
            // In equal mode, compute how many lanes visually fit (30px header + 18px overflow label)
            const visibleLanes = equalRows && rowHeight > 0 ? Math.max(0, Math.floor((rowHeight - 30 - 18) / 24)) : Infinity;

            return (
              <div
                key={wi}
                ref={wi === 0 ? firstRowRef : undefined}
                className={`relative flex flex-col flex-1 ${equalRows ? 'overflow-hidden' : ''} ${wi < weekRows.length - 1 ? 'border-b-2 border-gray-700/70 day:border-gray-300' : ''}`}
              >
                {/* Day cell backgrounds */}
                <div className="grid grid-cols-7 flex-1">
                  {week.map((cell, ci) => {
                    const isWeekend = ci === 0 || ci === 6;

                    if (cell.outside) {
                      return (
                        <div key={`out-${wi}-${ci}`} className={`p-1.5 h-full ${ci < 6 ? 'border-r-2 border-gray-700/70 day:border-gray-300' : ''} bg-gray-900/20 day:bg-gray-50`}
                          style={equalRows ? undefined : { minHeight: `${32 + eventAreaHeight + 4}px` }}>
                          <span className="text-[11px] text-gray-700/40 day:text-gray-300 p-0.5 select-none">{cell.day}</span>
                        </div>
                      );
                    }

                    const day = cell.day;
                    const isToday = today.date() === day && isCurrentMonth;
                    const isPast  = currentMonth.date(day).isBefore(today, 'day');
                    const dayEvts = eventsByDate[day] || [];
                    const totalDrones = dayEvts.reduce((s, e) => s + buffered(e), 0);
                    const heat = getHeat(totalDrones);
                    const hasOverflow = layout.overflowByCol[ci] > 0;

                    return (
                      <div
                        key={day}
                        ref={el => { cellRefs.current[day] = el; }}
                        role="button"
                        tabIndex={0}
                        onClick={() => { cancelPopover(); setSelectedDate(currentMonth.date(day).format('YYYY-MM-DD')); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cancelPopover(); setSelectedDate(currentMonth.date(day).format('YYYY-MM-DD')); } }}
                        onMouseEnter={() => startPopover(day)}
                        onMouseLeave={cancelPopover}
                        className={`
                          p-1.5 cursor-pointer group transition-all duration-200 relative h-full
                          ${ci < 6 ? 'border-r-2 border-gray-700/70 day:border-gray-300' : ''}
                          ${heat.bg} ${heat.glow} ${heat.ring}
                          ${isPast && !isToday ? 'opacity-50 hover:opacity-80 day:opacity-60 day:hover:opacity-90' : ''}
                          ${isWeekend && !dayEvts.length ? 'bg-gray-900/15 day:bg-gray-50/80' : ''}
                          hover:bg-gray-700/20 day:hover:bg-gray-100
                        `}
                        style={equalRows ? undefined : { minHeight: `${32 + eventAreaHeight + (hasOverflow ? 18 : 4)}px` }}
                      >
                        {/* Day number + drone badge */}
                        <div className="flex items-start justify-between">
                          <span className={`
                            text-[11px] leading-none select-none
                            ${isToday
                              ? 'bg-primary text-gray-900 w-6 h-6 rounded-full flex items-center justify-center font-extrabold cal-today-pulse'
                              : isPast
                                ? 'text-on-surface-disabled day:text-gray-400 p-0.5'
                                : 'text-on-surface-variant day:text-gray-700 p-0.5 group-hover:text-on-surface day:group-hover:text-gray-900 transition-colors font-medium'
                            }
                          `}>
                            {day}
                          </span>
                          {totalDrones > 0 && (
                            <span className={`
                              text-[8px] tabular-nums font-medium px-1.5 py-0.5 rounded-full
                              ${totalDrones >= 500 ? 'text-red-400/80 day:text-red-700 bg-red-500/10 day:bg-red-100'
                                : totalDrones >= 200 ? 'text-amber-400/70 day:text-amber-700 bg-amber-500/10 day:bg-amber-100'
                                : 'text-on-surface-variant day:text-gray-600 bg-surface-container-high day:bg-gray-100 group-hover:text-on-surface day:group-hover:text-gray-800'}
                            `}>
                              {totalDrones.toLocaleString()}
                            </span>
                          )}
                        </div>

                        {/* Overflow indicator at bottom */}
                        {(() => {
                          // Count events in this column that have a lane assigned
                          const placedInCol = layout.placements.filter(p => p.startCol <= ci && p.endCol >= ci).length;
                          // In fit-content mode, overflow comes from lane cap; in equal mode, from visual clipping
                          const laneCapOverflow = layout.overflowByCol[ci] || 0;
                          const clippedCount = placedInCol > visibleLanes ? placedInCol - visibleLanes : 0;
                          const totalHidden = laneCapOverflow + clippedCount;
                          if (totalHidden <= 0) return null;
                          return (
                            <div className="absolute bottom-0.5 left-0 right-0 text-center z-10">
                              <span className="text-[9px] text-primary/80 day:text-amber-700 font-medium group-hover:text-primary day:group-hover:text-amber-800 transition-colors bg-gray-900/80 day:bg-white/95 px-1.5 py-0.5 rounded-full backdrop-blur-sm day:ring-1 day:ring-gray-200">
                                +{totalHidden} more
                              </span>
                            </div>
                          );
                        })()}

                        {/* Today ring */}
                        {isToday && <div className="absolute inset-0 ring-1 ring-inset ring-primary/15 day:ring-primary/40 pointer-events-none" />}

                        {/* Popover */}
                        {popover && popover.day === day && dayEvts.length > 0 && (
                          <DayPopover
                            dateStr={currentMonth.date(day).format('YYYY-MM-DD')}
                            events={dayEvts}
                            tourMap={tourMap}
                            rect={popover.rect}
                            calendarRect={popover.calendarRect}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Spanning event bars overlay */}
                <div
                  className="absolute inset-x-0 grid grid-cols-7 content-start pointer-events-auto"
                  style={{
                    top: '30px',
                    rowGap: '2px',
                    gridAutoRows: '22px',
                    paddingLeft: '0',
                    paddingRight: '0',
                  }}
                >
                  {/* Tour lane backgrounds (rendered first, behind event bars). Tinted with
                      the tour's accent color so multiple tours in view stay distinguishable.
                      Gap dashed connectors are intentionally absent — dashed lines now signal
                      ship logistics; tour grouping reads as solid colored bg + chain icons. */}
                  {layout.tourConnectors.filter(c => c.lane < visibleLanes).map((conn, ci) => {
                    const isTourHL = highlightTourId && highlightTourId === conn.tourId;
                    const isTourDimmed = highlightTourId && highlightTourId !== conn.tourId;
                    const acc = accentForEvent({ tour_id: conn.tourId }, dayMode);
                    const bgColor   = acc && (isTourHL ? acc.laneBgHL   : isTourDimmed ? acc.laneBgDim   : acc.laneBg);
                    const ringColor = acc && (isTourHL ? acc.laneRingHL : isTourDimmed ? acc.laneRingDim : acc.laneRing);
                    return (
                      <div
                        key={`conn-${ci}`}
                        className={`pointer-events-none cal-bar-fade ring-2 ring-inset
                          ${conn.flatLeft  ? '' : 'rounded-l-[4px] ml-[2px]'}
                          ${conn.flatRight ? '' : 'rounded-r-[4px] mr-[2px]'}
                          ${isTourDimmed ? 'opacity-60' : ''}
                        `}
                        style={{
                          gridColumn: `${conn.startCol + 1} / ${conn.endCol + 2}`,
                          gridRow: conn.lane + 1,
                          backgroundColor: bgColor,
                          '--tw-ring-color': ringColor,
                        }}
                      />
                    );
                  })}
                  {/* Event bars */}
                  {layout.placements.filter(p => p.lane < visibleLanes).map((p, pi) => renderEventBar(p, pi))}
                </div>

              </div>
            );
          })}
          </div>
        </div>
        </FadeIn>
      )}

      {/* ── Legend ── */}
      <Reveal show={!loading}>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 px-1">
          <div className="flex items-center gap-3">
            <span className="text-[9px] text-on-surface-variant day:text-gray-500 uppercase tracking-[0.15em] font-bold">Load</span>
            {[['bg-sky-500/25 day:bg-sky-200 ring-sky-500/15 day:ring-sky-300', '<200'], ['bg-amber-500/25 day:bg-amber-200 ring-amber-500/15 day:ring-amber-300', '200+'], ['bg-red-500/25 day:bg-red-200 ring-red-500/15 day:ring-red-300', '500+']].map(([cls, lbl]) => (
              <span key={lbl} className="flex items-center gap-1.5">
                <span className={`w-3 h-1.5 rounded-sm ring-1 ring-inset ${cls}`} />
                <span className="text-[10px] text-on-surface-variant day:text-gray-600">{lbl}</span>
              </span>
            ))}
          </div>
          <div className="flex-1" />
          <span className="flex items-center gap-1.5">
            <span className="flex items-center w-8 h-2.5">
              <span className="w-2.5 h-2 rounded-sm bg-emerald-400/20 day:bg-emerald-100 ring-1 ring-inset ring-emerald-400/25 day:ring-emerald-300" />
              <span className="flex-1 h-px border-t border-dashed border-cyan-400/40 day:border-cyan-400" />
              <span className="w-2.5 h-2 rounded-sm bg-amber-400/20 day:bg-amber-100 ring-1 ring-inset ring-amber-400/25 day:ring-amber-300" />
            </span>
            <span className="text-[10px] text-on-surface-variant day:text-gray-600">Tour</span>
          </span>
          <p className="text-[10px] text-on-surface-variant day:text-gray-500 w-full sm:w-auto sm:ml-auto">Click any day for availability & drone planning</p>
        </div>
      </Reveal>

      {/* ── Availability modal ── */}
      {selectedDate && (
        <DateAvailabilityModal date={selectedDate} onClose={() => setSelectedDate(null)} />
      )}

      {/* ── Animations ── */}
      <style>{`
        @keyframes calBarEnter    { from { transform: scaleX(0.6) translateY(4px); } to { transform: none; } }
        @keyframes calTodayPulse  { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,204,51,0.3); } 50% { box-shadow: 0 0 12px 2px rgba(255,204,51,0.25); } }
        @keyframes calPopupEnter  { from { transform: scale(0.96) translateY(-4px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes calPopupSheet  { from { transform: translateY(100%); } to { transform: none; } }
        @keyframes calPopupFade   { from { opacity: 0; } to { opacity: 1; } }

        .cal-bar-enter   { animation: calBarEnter   300ms ease-out both; transform-origin: left center; }
        .cal-today-pulse { animation: calTodayPulse 3s ease-in-out infinite; }
        .cal-popup-enter { animation: calPopupEnter 140ms ease-out both; transform-origin: top left; }
        .cal-popup-sheet { animation: calPopupSheet 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .cal-popup-fade  { animation: calPopupFade  140ms ease-out both; }

        /* Symmetric 2s opacity transition (works in both dim and un-dim directions).
           Changing transition-duration via className simultaneously with the opacity
           value makes the browser skip the transition; keeping duration constant in
           a stable rule guarantees the fade always runs. The leave-debounce (80ms)
           is what gives the un-dim its "snappier" feel even though the fade itself
           takes 2s. Filter and transform stay quick (150ms) for snappy highlight pop. */
        .cal-bar-fade { transition: opacity 2000ms ease-out, filter 150ms ease-out, transform 150ms ease-out; }
      `}</style>

      <EventHoverCardPortal hover={hoverCard} />
      {popup && <EventClickPopup popup={popup} onClose={() => setPopup(null)} />}
    </PageLayout>
  );
};

export default EventCalendar;
